<template>
    <Teleport to="body">
        <div class="template-editor" role="dialog" aria-modal="true" :aria-label="`Edit ${heading}`">
            <header class="editor-header">
                <button class="back" title="Close" aria-label="Close" @click="requestClose()">←</button>
                <span class="icon sheet-icon"></span>
                <div class="title-block">
                    <div class="title-row">
                        <span class="title">{{ heading }}</span>
                        <span class="badge">Editing example rows</span>
                    </div>
                    <div v-if="content?.filename" class="subline">{{ content.filename }}</div>
                </div>
                <div class="header-actions">
                    <button class="btn" :disabled="saving" @click="requestClose()">Cancel</button>
                    <button class="btn primary" :disabled="saving || !content" @click="save()">
                        <LoaderItem v-if="saving" size="14px" />Save template
                    </button>
                </div>
            </header>

            <div v-if="confirmingClose" class="banner flex align-center gap-10">
                <span class="flex-1">You have unsaved changes. Close the editor and discard them?</span>
                <button class="btn" @click="confirmingClose = false">Keep editing</button>
                <button class="btn danger" @click="emit('close')">Discard changes</button>
            </div>
            <div v-else class="note">
                These rows are saved into the template file from row 3 down. The header rows, dropdown lists,
                hidden sheets and formatting of the file stay as they are, so the downloaded Excel keeps all its dropdowns.
                To change column headers, upload a new file with <b>Replace Template</b>.
            </div>

            <div v-if="loading" class="state"><LoaderItem size="24px" /></div>
            <div v-else-if="!content" class="state">The template could not be opened.</div>
            <div v-else-if="!content.columns.length" class="state">No column headers were found in row 2 of this template.</div>
            <FiuGrid
                v-else
                ref="grid"
                :template="gridTemplate"
                :initialData="content.rows"
                :editorMode="true"
                :minRows="30"
                height="calc(100vh - 230px)"
            />
        </div>
    </Teleport>
</template>

<script setup>
    import { ref, computed, onMounted } from 'vue';
    import BO_API from '@/api/bo_api.js';
    import store from '@/store';
    import FiuGrid from '@/components/FiuGrid.vue';
    import LoaderItem from '@/components/LoaderItem.vue';

    const props = defineProps({
        type: { type: String, required: true },
        title: { type: String, default: '' },
    });
    // saved: { filesize, uploadDate } of the new template file
    const emit = defineEmits(['close', 'saved']);

    const grid = ref(null);
    const content = ref(null); // { type, label, filename, columns, rows }
    const loading = ref(true);
    const saving = ref(false);
    const confirmingClose = ref(false);
    let savedRows = '[]'; // normalized rows last loaded/saved, to detect unsaved edits

    const heading = computed(() => content.value?.label || props.title || props.type);
    // Stable object: FiuGrid rebuilds whenever its template prop changes
    const gridTemplate = computed(() => content.value && {
        type: content.value.type,
        label: content.value.label,
        isPlayerTemplate: false,
        columns: content.value.columns,
    });

    // Rows of exactly one value per column, empty rows dropped
    function normalize(rows) {
        const width = content.value?.columns.length || 0;
        return (rows || [])
            .map(row => Array.from({ length: width }, (_, x) => {
                const value = row?.[x];
                return value === null || value === undefined ? '' : String(value).trim();
            }))
            .filter(row => row.some(value => value !== ''));
    }
    const currentRows = () => normalize(grid.value?.getSnapshot()?.data);
    const hasChanges = () => !!grid.value && JSON.stringify(currentRows()) !== savedRows;

    function requestClose() {
        if (saving.value) return;
        if (hasChanges()) confirmingClose.value = true;
        else emit('close');
    }

    async function save() {
        if (!grid.value || saving.value) return;
        const rows = currentRows();
        saving.value = true;
        const response = await BO_API.saveTemplateContent(props.type, rows);
        saving.value = false;
        if (response?.status !== 'success') return;
        savedRows = JSON.stringify(rows);
        confirmingClose.value = false;
        store.dispatch('addNotification', { type: 'success', text: `Template saved with ${response.rows} example row${response.rows === 1 ? '' : 's'}` });
        emit('saved', response.template);
        emit('close');
    }

    // No Escape-to-close: the grid uses Escape to cancel cell edits and dropdowns
    onMounted(async () => {
        content.value = await BO_API.getTemplateContent(props.type);
        if (content.value) savedRows = JSON.stringify(normalize(content.value.rows));
        loading.value = false;
    });
</script>

<style scoped lang="scss">
// Full screen over the admin layout (like TemplatePreview), under toasts (9999)
.template-editor {
    --muted: #5f6368;
    position: fixed;
    inset: 0;
    z-index: 1500;
    background: var(--app-background, #F6F6FC);
    padding: 10px 16px 12px;
    box-sizing: border-box;
    text-align: left;
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-width: 0;
    overflow: auto;
}
.state {
    padding: 60px;
    display: flex;
    justify-content: center;
    color: var(--muted);
}
.editor-header {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
}
.back {
    font-size: 22px;
    color: var(--muted);
    width: 36px;
    height: 36px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;
    cursor: pointer;
    &:hover { background: #eef1f7; }
}
.icon {
    width: 28px;
    height: 28px;
    flex: none;
    background-repeat: no-repeat;
    background-position: center;
    background-size: contain;
}
.sheet-icon {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%230073ef' d='M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 8h-8v8H9v-8H5V9h4V5h2v4h8v2z'/%3E%3C/svg%3E");
}
.title-block {
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
}
.title-row {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    margin-bottom: 4px;
}
.title {
    font-size: 18px;
    color: #1f1f1f;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.subline {
    font-size: 13px;
    color: var(--muted);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.badge {
    flex: none;
    white-space: nowrap;
    font-size: 12px;
    line-height: 18px;
    background: #e6f4ea;
    color: #137333;
    border-radius: 10px;
    padding: 1px 8px;
}
.header-actions {
    display: flex;
    gap: 8px;
    flex: none;
}
.btn {
    width: auto;
    padding: 7px 14px;
    border-radius: 8px;
    background: white;
    color: #1f1f1f;
    border: 1px solid var(--border-color);
    font-weight: 600;
    gap: 6px;
    white-space: nowrap;
    &:hover { background: #f1f4fb; }
    &.primary {
        background: var(--primary-color);
        border-color: var(--primary-color);
        color: white;
        &:hover { background: #005cc0; }
    }
    &.danger {
        background: #D06A69;
        border-color: #D06A69;
        color: white;
        &:hover { background: #b85554; }
    }
    &:disabled { background: #ccc; border-color: #ccc; color: #666; }
}
.banner, .note {
    padding: 10px 14px;
    border-radius: 8px;
    font-size: 14px;
}
.banner {
    background: #fef7e0;
    border: 1px solid #f9ab00;
}
.note {
    background: #fff;
    border: 1px solid var(--border-color, #d0d3d9);
    color: var(--muted);
}
</style>
